<!--
  Copyright (c) MobiFactory. All rights reserved.
  This source code is the property of MobiFactory. It is exclusively available to customers who have a valid user license.
  See LICENSE.txt file in the project root for full license information.
-->

<template>
  <div>
    <el-tooltip :disabled="!showTooltip" :content="user.name" placement="bottom" effect="light">
      <el-avatar
        v-if="photoUrl"
        icon="el-icon-user-solid"
        class="crm-badge-photo"
        :class="photoSizeClass"
        :src="photoUrl"
        :alt="user.name"
      />
      <div v-else-if="isExternalUser" class="crm-badge-text" :class="textSizeClass">
        {{ $t("UserBadge.ExternalUser") }}
      </div>
      <div v-else class="crm-badge-text" :class="textSizeClass" :style="{ 'background-color': userBackgroundColor }">
        {{ initials }}
      </div>
    </el-tooltip>
  </div>
</template>

<script>
import ColorHash from "color-hash";
import usersApi from "../../api/user/users";
import { User } from "../../model/User";
export default {
  name: "CrmUserBadge",
  props: {
    user: Object,
    size: String,
    showTooltip: {
      type: Boolean,
      default: false,
    },
  },
  /**
   * Gets the component data
   * @returns {object} Component data
   */
  data() {
    return {
      activeUsers: [],
    };
  },
  computed: {
    /**
     * Gets the initials from user name
     * @returns {string} the initials
     */
    initials() {
      if (!this.user.name) {
        return "";
      }

      const nameParts = this.user.name.trim().split(" ");
      if (nameParts.length > 1) {
        return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
      } else {
        return nameParts[0][0].toUpperCase();
      }
    },

    /**
     * Gets the size class modifier
     * @returns {string|null}
     */
    textSizeClass() {
      if (this.size) {
        return "crm-badge-text--" + this.size;
      }
      return null;
    },

    /**
     * Gets the size class modifier
     * @returns {string|null}
     */
    photoSizeClass() {
      if (this.size) {
        return "crm-badge-photo--" + this.size;
      }
      return null;
    },

    /**
     * Gets user photo. If not specified, lookup in the reference data users list
     * @returns {string|null} photo url
     */
    photoUrl() {
      if (this.user.photoUrl) {
        return this.user.photoUrl;
      }
      const refUser = this.activeUsers.find((u) => u.id === this.user.id);
      if (refUser) {
        return refUser.photoUrl;
      }

      return null;
    },

    /**
     * Indicates if the user is the external user
     * @returns {boolean} true if the user is external user
     */
    isExternalUser() {
      return this.user.id === User.EXTERNAL_USER_ID;
    },

    /**
     * Gets the background corresponding to the user
     * @returns {null|string} color
     */
    userBackgroundColor() {
      if (!this.user.name) {
        return null;
      }
      const colorHash = new ColorHash();
      return colorHash.hex(this.user.name);
    },
  },

  /**
   * When component is created
   */
  async created() {
    let result = await usersApi.getUsers();
    if (result.isSuccess) {
      this.activeUsers = result.data;
    }
  },
};
</script>
